import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { isAdminUser } from '../shared/utils/admin.util';
import { UserProfile } from '../types/UserProfile';
import { AuthService } from './auth.service';
import { IProfileManagementService } from './business/IProfileManagementService';
import { CoreService } from './core.service';

@Injectable({
  providedIn: 'root',
})
export class AppsOrPagesGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
  ) {}

  async canActivate(): Promise<boolean> {
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (currentUser) {
        return true;
      } else {
        this.router.navigate(['/landing']);
        return false;
      }
    } catch (error) {
      console.error('Authentication check failed', error);
      this.router.navigate(['/landing']);
      return false;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class CustomerExistsGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private profileService: IProfileManagementService,
    private _coreService: CoreService,
  ) {}

  async canActivate(): Promise<boolean> {
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (currentUser) {
        try {
          const userProfile = await this.profileService.getUserProfile();
          if (isAdminUser(userProfile, currentUser)) {
            return true;
          }

          // Check if user is an affiliate manager first
          const isAffiliateManager = userProfile?.affiliateManager === true;

          let profile = await this.profileService.getProfile();
          // If cached profile shows customer status INACTIVE then forcefully fetch fresh profile/customer
          if (profile?.status !== 'active') {
            profile = await this.profileService.getProfile(true);
          }
          if (
            profile == undefined ||
            profile == null ||
            profile.businessId == undefined ||
            profile.status !== 'active'
          ) {
            // Only redirect to KYC onboarding if user is not an affiliate manager
            if (!isAffiliateManager) {
              this.router.navigate(['/kyc-onboarding']);
            }
          }
          return true;
        } catch (e: unknown) {
          // Check if this is an affiliate manager who might not have a business profile
          try {
            const userProfile = await this.profileService.getUserProfile();
            if (isAdminUser(userProfile, currentUser)) {
              return true;
            }
            const isAffiliateManager = userProfile?.affiliateManager === true;
            if (isAffiliateManager) {
              return true; // Allow affiliate managers even if profile fetch fails
            }
          } catch (userProfileError) {
            if (isAdminUser(null, currentUser)) {
              return true;
            }
          }

          this._coreService.onFailureToaster(
            'Error in fetching your information',
            e,
          );
          return false;
        }
      } else {
        // If the user is not authenticated, allow access to login/signup routes
        return true;
      }
    } catch (error) {
      // If the user is not authenticated (or session error occurs), allow access
      return true;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthRedirectGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private _coreService: CoreService,
    private profileService: IProfileManagementService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    const currentRoute = state.url; // Get the current route
    console.log(currentRoute);
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (currentUser) {
        // Check if user is an affiliate manager first
        let userProfile;
        let isAffiliateManager = false;
        let isAdmin = false;
        try {
          userProfile = await this.profileService.getUserProfile();
          isAffiliateManager = userProfile?.affiliateManager === true;
          isAdmin = isAdminUser(userProfile, currentUser);
        } catch (error) {
          console.error('Error getting user profile:', error);
          isAdmin = isAdminUser(null, currentUser);
        }

        if (isAdmin) {
          if (
            currentRoute == '/authentication/login' ||
            currentRoute == '/authentication/signup'
          ) {
            return true;
          }

          this.router.navigate(['/admin']);
          return false;
        }

        if (
          currentRoute == '/authentication/login' ||
          currentRoute == '/authentication/signup'
        ) {
          return true;
        }

        // This prevents bypass when profile fetch fails for new users
        if (currentRoute == '/kyc-onboarding') {
          if (!userProfile || !userProfile.phoneNumber) {
            this.router.navigate(['/authentication/signup']);
            return false;
          }
          return true;
        }

        let profile;
        try {
          profile = await this.profileService.getProfile();
        } catch (error) {
          // If affiliate manager, allow them to continue even without business profile
          if (isAffiliateManager) {
            return true;
          }
          // For new users without business profile, redirect to KYC onboarding and check if phone number verified
          if (currentRoute != '/kyc-onboarding') {
            if (!userProfile || !userProfile.phoneNumber) {
              this.router.navigate(['/authentication/signup']);
              return false;
            }

            this.router.navigate(['/kyc-onboarding']);
            return false;
          }

          this.router.navigate(['/authentication/signup']);
          return false;
        }

        if (profile?.phoneNumbers?.length == 0) {
          this.router.navigate(['/authentication/login']);
          return false;
        }

        // Handle affiliates route specially for affiliate managers
        if (currentRoute == '/affiliates' && isAffiliateManager) {
          return true;
        }

        if (
          profile == undefined ||
          profile == null ||
          profile.businessId == undefined
        ) {
          if (currentRoute != '/kyc-onboarding') {
            // Only redirect to KYC onboarding if user is not an affiliate manager
            if (!isAffiliateManager) {
              this.router.navigate(['/kyc-onboarding']);
            } else {
              // For affiliate managers, allow them to access their permitted routes
              return true;
            }
          }
        } else {
          this.router.navigate(['/dashboard']);
        }
        // If the user is authenticated, redirect to the dashboard or another route
        return false;
      } else {
        // If the user is not authenticated, allow access to login/signup routes
        return true;
      }
    } catch (error) {
      this._coreService.onFailureToaster(
        'Error loading your organization information',
        error,
      );
      // If the user is not authenticated (or session error occurs), allow access
      return true;
    }
  }
}

/**
 * Restricts affiliate management routes to users with affiliateManager === true.
 */
@Injectable({
  providedIn: 'root',
})
export class AffiliateManagerGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private profileService: IProfileManagementService,
    private coreService: CoreService,
  ) {}

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Promise<boolean> {
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (!currentUser) {
        this.router.navigate(['/authentication/login']);
        return false;
      }

      // Get user profile to check affiliate manager status
      const userProfile = await this.profileService.getUserProfile();

      // Check if user is an affiliate manager
      if (userProfile && userProfile.affiliateManager === true) {
        return true;
      } else {
        // User is not an affiliate manager, redirect to dashboard
        this.coreService.onFailureToaster(
          'Access Denied',
          'You do not have permission to access the Affiliates page. This page is only available to Affiliate Managers.',
        );
        this.router.navigate(['/dashboard']);
        return false;
      }
    } catch (error) {
      this.coreService.onFailureToaster('Error checking permissions', error);
      this.router.navigate(['/dashboard']);
      return false;
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class AdminGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private profileService: IProfileManagementService,
    private coreService: CoreService,
  ) {}

  async canActivate(): Promise<boolean> {
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (!currentUser) {
        this.router.navigate(['/authentication/login']);
        return false;
      }

      const userProfile = await this.profileService.getUserProfile();
      if (isAdminUser(userProfile, currentUser)) {
        return true;
      }

      this.coreService.onFailureToaster(
        'Access Denied',
        'You do not have permission to access the Admin Dashboard.',
      );
      this.router.navigate(['/dashboard']);
      return false;
    } catch (error) {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (isAdminUser(null, currentUser)) {
        return true;
      }

      this.coreService.onFailureToaster('Error checking permissions', error);
      this.router.navigate(['/dashboard']);
      return false;
    }
  }
}

export async function canAccessFiraCalculator(
  userProfile: UserProfile | null | undefined,
  profileService: IProfileManagementService,
): Promise<boolean> {
  if (userProfile?.affiliateManager !== true) {
    return false;
  }

  const customerId = userProfile.currentContextCustomerId?.trim();
  if (!customerId) {
    return true;
  }

  const isActive = await profileService.isContextCustomerActive(customerId);
  return !isActive;
}

@Injectable({
  providedIn: 'root',
})
export class FiraCalculatorGuard implements CanActivate {
  constructor(
    private router: Router,
    private authService: AuthService,
    private profileService: IProfileManagementService,
    private coreService: CoreService,
  ) {}

  async canActivate(): Promise<boolean> {
    try {
      const currentUser = await this.authService.getCurrentAuthenticatedUser();
      if (!currentUser) {
        this.router.navigate(['/authentication/login']);
        return false;
      }

      const userProfile = await this.profileService.getUserProfile();
      if (await canAccessFiraCalculator(userProfile, this.profileService)) {
        return true;
      }

      this.coreService.onFailureToaster(
        'Access Denied',
        'FIRA Calculator is only available to Affiliate Managers without an active business.',
      );
      this.router.navigate(['/dashboard']);
      return false;
    } catch (error) {
      this.coreService.onFailureToaster('Error checking permissions', error);
      this.router.navigate(['/dashboard']);
      return false;
    }
  }
}
